<template>
  <div
    v-if="readOnly"
    :data-test-id="dataTestId"
    class="list-view-class"
  >
    {{ value }}
  </div>
  <div
    v-if="showField"
  >
    <span
      v-if="!showInTable && !readOnly"
      class="input-group my-2"
    >{{ floatOptions.label[currentLanguage] }}
      <span
        v-if="isMandatory"
        class="ms-2"
        style="color: red"
      >
        *
      </span>
    </span>
    <div
      v-if="!showInTable"
      :class="{ 'd-block': isMissing || !isValid }"
      class="invalid-feedback p-0 text-xs"
    >
      {{ errorText }}
    </div>
    <input
      :id="dataTestId"
      v-model="value"
      :data-test-id="dataTestId"
      :class="{ 'mandatory-field': isMandatory && isMissing,
                'invalid-field':!isValid}"
      class="form-control form-input"
      type="number"
      step="0.01"
      inputmode="decimal"
      :placeholder="placeHolder"
      name="float-input"
      :disabled="isReadOnly || isEditNotAllowed"
      @change="handleChange"
    >
    <v-tooltip
      v-if="showInTable && value!=null"
      activator="parent"
      open-delay="600"
      location="top"
    >
      {{ value }}
    </v-tooltip>
  </div>
</template>

<script>
import { ref, computed, toRef } from 'vue';
import { useI18n } from 'vue-i18n';
import processExpr from '../../composables/processExprs';
import controlUtility from '../../composables/controlUtility';
export default {
  name: 'WfmFloat',
  props: {
    params: {
      type: Object,
      default: null,
    }
  },
  emits: ['input-change', 'invalid-check', 'mandatory-check'],

  setup(props, context) {
    //variables
    const i8vue = useI18n()
    const currentLanguage = i8vue.locale.value
    const value = ref(null);
    const showInTable = props.params?.showInTable ? true : false;
    const readOnly = props.params?.readOnly ? true : false;
    const floatOptions = props.params.fieldOptions
    const floatParams = toRef(props.params.data)
    const folderName = floatOptions?.path?.split('.')[0]
    const idOfTheFolder = floatParams.value?.[folderName]?.[0]?.id ? floatParams.value?.[folderName]?.[0]?.id : floatParams.value?.id
    const fieldPath = floatOptions?.path
    const propertyName = floatOptions?.name;
    value.value = floatParams.value?.[propertyName] ? floatParams.value?.[propertyName] : floatParams.value?.[folderName]?.[0]?.[propertyName];
    value.value = value.value?.toFixed(2)
    const isValid = ref(true)
    const valueEntered = ref(false)

    const mandatoryStatus = props.params.mandatoryStatus
    const isEditNotAllowed = props.params.isEditNotAllowed
    const missingFields = props.params.missingFields

    const currentFolder = ref(props.params.currentFolder)
    const mandatoryFields = props.params.mandatoryFields
    const invalidFields = props.params.invalidFields
    const { invalidCheck, mandatoryCheck } = controlUtility(invalidFields, mandatoryFields)
    // const folderFields = ref(props.folderFields || props.params.fieldParams.folderFields)
    const { processReadOnly, processVisibleExpr, processMandatoryExpr, processDefaultValue, processValidExpr} = processExpr(currentFolder.value, floatParams.value, showInTable)

    //computed
    const placeHolder = computed(() => {
      return floatOptions.placeHolder ? floatOptions.placeHolder : floatOptions.label.eng
    })
    const isMissing = computed(() => {
      if (missingFields?.value?.includes(dataTestId.value)) {
        if (valueEntered.value) {
          return false
        }
        else {
          return true
        }
      }
      else {
        return false
      }
    })
    const isVisible = computed(() => {
      const retVal = processVisibleExpr(floatOptions.visible_expr)
      return retVal
    })
    const isReadOnly = computed(() => {
      const retVal = processReadOnly(floatOptions.readonly_expr)
      return retVal
    })
    const isMandatory = computed(() => {
      const retVal = processMandatoryExpr(floatOptions.mandatory_expr) && isVisible.value
      if (!showInTable) {
        mandatoryCheck({
          propertyName: dataTestId.value,
          value: retVal,
        })
      }
      return retVal
    })
    const defaultValue = computed(() => {
      const retVal = processDefaultValue(floatOptions.default_value)
      return retVal
    });
    const errorText = computed(() => {
      if (!isValid.value) {
        return 'Invalid Input'
      }
      if (isMissing.value) {
        return 'Mandatory field'
      }
      else {
        return ''
      }
    });
    const showField = computed(() => {
      if (!mandatoryStatus?.value) {
        return true
      }
      else {
        if (isMandatory.value) {
          return true
        }
        else {
          return false
        }
      }
    })
    const dataTestId = computed(() => {
      if (showInTable || readOnly) {
        const rowIndex = props.params.node.rowIndex
        const tableName = props.params.tableName
        return `${currentFolder.value.name}/${tableName}/${propertyName}/${rowIndex}`
      } else {
        if (currentFolder.value !== null) {
          return `${currentFolder.value.name}/${propertyName}`

        }
        else {
          return `${floatOptions.type}/${propertyName}`
        }
      }
    })

    //methods
    function handleChange() {
      try {
        const newValue = value.value;
        if (newValue) {
          valueEntered.value = true
        }
        if (floatOptions.valid_expr !== undefined) {
          isValid.value = processValidExpr(floatOptions.valid_expr, newValue)
        }
        if (isValid.value) {
        // context.emit("invalid-check", { propertyName: propertyName, value: false })
          invalidCheck({ propertyName: propertyName, value: false })

          //if the data is changed in ag grid
          if (showInTable) {
            floatParams.value[propertyName] = newValue
            props.params.cellValueChanged(floatParams.value)
          }
          //else emit event to control
          else {
            context.emit('input-change', { path: fieldPath, value: newValue, folderId: idOfTheFolder, mandatory: floatOptions.mandatory_expr }
            );
          }
        }
        else {
        // context.emit("invalid-check", { propertyName: propertyName, value: true })
          invalidCheck({ propertyName: propertyName, value: true })

        }
      } catch (error) {
        throw error
      }

    }
    if (defaultValue.value) {
      getDefaultValue()
    }
    function getDefaultValue() {
      const defaultSelectedValue = defaultValue.value
      value.value = defaultSelectedValue
      handleChange()
    }

    return {
      value,
      handleChange,
      floatOptions,
      showInTable,
      readOnly,
      currentLanguage,
      isMandatory,
      isReadOnly,
      isMissing,
      isValid,
      errorText,
      showField,
      isEditNotAllowed,
      placeHolder,
      dataTestId
    };
  },
};
</script>

<style scoped>
.input-style {
  width: 100%;
  color: red;
}

.form-input {
  min-height: 5px;
  width: 100%;
  background-color: var(--controls-background-color);
  color:var(--controls-font-color);
  border:1px solid var(--controls-border-color);
}
.input-group-text {
  background-color: antiquewhite;
}

.mandatory-field {
  border: 1px solid rgb(214, 65, 85) !important;
  box-shadow: rgba(0, 0, 0, 0.1) 0px 4px 12px;
  color: black
}
.invalid-field{
    border: 1px solid rgb(214, 65, 85) !important;
  box-shadow: rgba(0, 0, 0, 0.1) 0px 4px 12px;
  color: black

}
</style>
